import { useState, ChangeEvent } from 'react';
import { useT } from '../i18n';
import {
  Input, Checkbox, Dropdown, Option, Spinner,
  makeStyles, tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  field: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  label: { fontSize: '13px', fontWeight: '600', color: tokens.colorNeutralForeground1 },
  description: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  error: { fontSize: '12px', color: tokens.colorPaletteRedForeground1 },
  hint: { fontSize: '11px', color: tokens.colorNeutralForeground4, marginTop: '4px' },
});

export type FieldType = 'boolean' | 'number' | 'string' | 'enum' | 'range';

interface ConfigFormFieldProps {
  type: FieldType;
  label: string;
  description?: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  options?: readonly string[];
  /** Texto visible por opción (enum). Si falta una opción, se muestra su valor crudo. */
  optionLabels?: Readonly<Record<string, string>>;
  min?: number;
  max?: number;
  error?: string;
  loading?: boolean;
}

export function ConfigFormField({
  type, label, description, value, onChange, disabled = false,
  options, optionLabels, min, max, error, loading = false,
}: ConfigFormFieldProps) {
  const s = useStyles();
  const t = useT();
  const [touched, setTouched] = useState(false);

  const showError = touched && error;

  switch (type) {
    case 'boolean':
      return (
        <div className={s.field}>
          <Checkbox
            checked={value === true}
            onChange={(_, d) => onChange(d.checked)}
            label={label}
            disabled={disabled || loading}
          />
          {description && <div className={s.description}>{description}</div>}
          {showError && <div className={s.error}>{error}</div>}
        </div>
      );

    case 'enum':
      return (
        <div className={s.field}>
          <label className={s.label}>{label}</label>
          {description && <div className={s.description}>{description}</div>}
          <Dropdown
            value={String(value) || ''}
            onOptionSelect={(_, d) => onChange(d.optionValue)}
            disabled={disabled || loading}
            aria-label={label}
          >
            {options?.map(opt => (
              <Option key={opt} value={opt}>
                {optionLabels?.[opt] ?? opt}
              </Option>
            ))}
          </Dropdown>
          {min !== undefined && max !== undefined && (
            <div className={s.hint}>
              {min === max ? `${t.common.valueLabel}: ${min}` : `${t.common.rangeLabel}: ${min}–${max}`}
            </div>
          )}
          {showError && <div className={s.error}>{error}</div>}
        </div>
      );

    case 'number':
    case 'range':
      return (
        <div className={s.field}>
          <label className={s.label}>{label}</label>
          {description && <div className={s.description}>{description}</div>}
          <Input
            type="number"
            value={String(value || '')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value ? Number(e.target.value) : '')}
            disabled={disabled || loading}
            onBlur={() => setTouched(true)}
            aria-label={label}
          />
          {min !== undefined && max !== undefined && (
            <div className={s.hint}>
              {t.common.rangeLabel}: {min}–{max}
            </div>
          )}
          {loading && <Spinner size="tiny" />}
          {showError && <div className={s.error}>{error}</div>}
        </div>
      );

    case 'string':
    default:
      return (
        <div className={s.field}>
          <label className={s.label}>{label}</label>
          {description && <div className={s.description}>{description}</div>}
          <Input
            value={String(value || '')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value || '')}
            disabled={disabled || loading}
            onBlur={() => setTouched(true)}
            aria-label={label}
          />
          {showError && <div className={s.error}>{error}</div>}
        </div>
      );
  }
}
